import React, { useContext, useMemo, useState } from 'react';

import { commodityApis, dashboardCommodityApis } from '@apis/next/commodityApis';
import { displayAxiosError } from '@apis/next/errors';
import { Button } from '@components/Button';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { CustomsInstructionContext } from '@components/utilities/Bookings/helpers';
import { CustomsInstructionContext as CustomsInstructionContext2 } from '@components/utilities/Bookings/helpers2';
import { InstructionHint } from '@components/utilities/Bookings/instructionComponents';
import { CommodityFilter } from '@components/utilities/Commodity/CommodityFilter';
import { CommodityPhotoBrowser } from '@components/utilities/Commodity/CommodityPhotoBrowser';
import { CommodityPhotoCover } from '@components/utilities/Commodity/CommodityPhotoCover';
import { commodityStrings } from '@data/commodities';
import { useIsAdmin } from '@hooks/useIsAdmin';
import { Commodity } from '@models/Commodity';
import { AdminCommodityForm, CommodityHelpers } from '@models/next/Commodity';
import { MobxZodArrayField, getDecodeResultOr } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { ZodArray, ZodNumber } from 'zod';

const useStyles = makeMuiStyles((theme, common) => ({
  commodityArrayInputContainer: {
    ...common.flexColumn,
    flex: 1,
    rowGap: '0.5rem',
    overflow: 'hidden',
  },
  actionsContainer: {
    ...common.flexInline,
    flexWrap: 'wrap',
    columnGap: '1.5rem',
    paddingTop: '1rem',
    paddingBottom: '1rem',
    paddingLeft: 24,
    paddingRight: 24,
    [theme.breakpoints.down('sm')]: {
      columnGap: '8rem',
      rowGap: '0.5rem',
    },
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
    },
  },
  commodityDisplay: {
    ...common.flexColumn,
    ...common.middleText,
    borderRadius: 2,
    padding: '0.5rem 1rem',
    border: `1px solid ${theme.palette.grey[400]}`,
    minWidth: '18rem',
    '&$admin': {
      ...common.backgroundPrimaryAlpha3,
      border: 'initial',
    },
  },
  commodityDisplayTitle: {
    ...common.textBlack,
    display: 'flex',
    columnGap: '1.25rem',
  },
  editLinkContainer: {
    ...common.flexInline,
    ...common.justifyEnd,
    flexBasis: '95px',
  },
  title: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordWrap: 'break-word',
  },
  errorMessage: {
    marginTop: '0.5rem',
  },
  multilineContainer: {
    flex: 1,
    overflow: 'hidden',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    display: '-webkit-box',
  },
  commodityInfoContainer: {
    ...common.smallerText,
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: '0.5rem',
    marginBottom: '0.5rem',
    wordBreak: 'break-all',
  },
  hsCode: {
    ...common.smallerText,
    backgroundColor: theme.palette.grey[200],
    borderRadius: 2,
    border: `1px solid ${theme.palette.grey[400]}`,
    padding: '4px 8px !important',
  },
  admin: {},
  commodityHSCodeInput: {
    ...common.middleText,
    backgroundColor: 'transparent',
    outline: 'none',
    border: `1px solid ${theme.palette.grey[400]}`,
    padding: '4px !important',
    flex: 1,
    '&:focus': {
      backgroundColor: 'white',
    },
  },
  commodityHSCodeButton: {
    height: '30px !important',
  },
  commodityHSCodeForm: {
    ...common.flexInline,
  },
  hint: {
    ...common.textGray700,
    ...common.smallerText,
    marginTop: '0.5rem',
  },
}));

const strings = createStrings({
  ja: {
    selectFromCommodities: '商品情報の登録',
    selectFromCustomerCommodities: 'お客さんの輸入商品一覧から選択する',
    noExplanation: '商品説明なし',
    filledByD2d: 'D2D担当者記入欄',
    serverError: '読込エラー：',
    commodityHint: '＊商品が多い場合、カタログごとアップロードしていただいても構いません。',
    printPdf: 'PDFファイルを作成する',
  },
});

interface CommodityHSCodeFormProps {
  commodity: Commodity;
}

const CommodityHSCodeForm: React.FC<CommodityHSCodeFormProps> = observer((props) => {
  const { commodity } = props;
  const { id } = commodity;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const patchCommodity = dashboardCommodityApis.usePatch('/dashboard/commodity/:id', {
    params: { id },
  });

  const form = useForm(AdminCommodityForm, {
    initialOutput: useMemo(() => CommodityHelpers.mapToAdminForm(commodity), [commodity]),
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const handleSubmit = () =>
    form.handleSubmit(async () => {
      const parsed = form.parsed;
      if (parsed.success) {
        await patchCommodity.mutateAsync(parsed.data);
      }
    });

  return (
    <div>
      <div className={classes.commodityHSCodeForm} tabIndex={0}>
        <input
          className={classes.commodityHSCodeInput}
          placeholder={commodityStrings.hs_code}
          {...form.bindField(fields.hs_code)}
          onKeyDown={async (e) => {
            // To avoid the event propagting to parent form,
            // and submit the form by accident.
            if (e.key === 'Enter') {
              e.preventDefault();
              await handleSubmit();
            }
          }}
        />
        <Button
          text={formStrings.update}
          color="primary"
          size="small"
          type="button"
          className={classes.commodityHSCodeButton}
          onClick={handleSubmit}
          isLoading={patchCommodity.isLoading}
        />
      </div>

      <ErrorMessages field={fields.hs_code} />

      {!!patchCommodity.error && (
        <div
          className={clsx(classes.smallerText, classes.textError)}
          style={{ marginTop: '0.5rem' }}
        >
          {displayAxiosError(patchCommodity.error) ?? String(patchCommodity.error)}
        </div>
      )}

      {patchCommodity.isSuccess && (
        <div
          className={clsx(classes.smallerText, classes.textPrimary)}
          style={{ marginTop: '0.5rem' }}
        >
          {formStrings.saved}
        </div>
      )}
    </div>
  );
});

export interface CommodityArrayItemProps {
  commodity?: Commodity;
  id: number;
  onDelete: () => void;
}

export const CommodityArrayItem: React.FC<CommodityArrayItemProps> = (props) => {
  const { commodity, id, onDelete } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { isEditing, type } = useContext(CustomsInstructionContext);

  const commodityQuery =
    type == 'admin'
      ? dashboardCommodityApis.useGet('/dashboard/commodity/:id', {
          params: {
            id,
          },
        })
      : commodityApis.useGet('/commodity/:id', {
          params: {
            id,
          },
        });

  const [showPhotoBrowser, setShowPhotoBrowser] = useState(false);

  const finalCommodity = commodityQuery.data ?? commodity;

  return (
    <>
      <CommodityPhotoBrowser
        open={showPhotoBrowser}
        urls={finalCommodity?.images ?? []}
        onClose={() => setShowPhotoBrowser(false)}
      />
      <div className={clsx(classes.commodityDisplay, type === 'admin' && classes.admin)}>
        <div className={classes.commodityDisplayTitle}>
          <div title={finalCommodity?.name_en} className={classes.title}>
            {finalCommodity?.name_en}
          </div>

          {isEditing && (
            <div className={classes.editLinkContainer}>
              {type === 'client' && (
                <a
                  className={clsx(classes.link, classes.smallerText, classes.whitespacePre)}
                  href={`${url('/utilities/Commodity/CommodityDetails')}?id=${
                    finalCommodity?.id
                  }&action=edit`}
                  target="_blank"
                >
                  {formStrings.edit}
                </a>
              )}
              <div style={{ width: 8, flexShrink: 0 }} />
              <a
                className={clsx(
                  classes.link,
                  classes.smallerText,
                  classes.textGray500Important,
                  classes.whitespacePre,
                )}
                onClick={onDelete}
              >
                {formStrings.delete}
              </a>
            </div>
          )}
        </div>

        <div className={classes.flexInline} style={{ columnGap: 20 }}>
          <div style={{ flex: 1, minWidth: 0, flexShrink: 1 }} className={classes.flexColumn}>
            <div className={classes.commodityInfoContainer}>
              <div
                className={classes.middleText}
                style={{ marginBottom: '0.25rem', gridColumn: 'span 2' }}
              >
                {finalCommodity?.name_ja}
              </div>

              <div>{commodityStrings.commodity_no}:</div>
              <div>{finalCommodity?.commodity_no || '-'}</div>

              <div>{commodityStrings.memo}:</div>
              <div>{finalCommodity?.memo || '-'}</div>

              <div>{commodityStrings.explanation}:</div>
              <div>{finalCommodity?.explanation || '-'}</div>

              <div>{commodityStrings.url}:</div>
              <div>
                {finalCommodity?.url ? (
                  <a
                    href={finalCommodity.url}
                    target="_blank"
                    className={classes.link}
                    style={{ wordBreak: 'break-all' }}
                  >
                    {finalCommodity.url}
                  </a>
                ) : (
                  '-'
                )}
              </div>

              <div>{commodityStrings.usage}:</div>
              <div>{finalCommodity?.usage || '-'}</div>

              <div>{commodityStrings.material}:</div>
              <div>{finalCommodity?.material || '-'}</div>
            </div>

            {type === 'admin' && finalCommodity && (
              <CommodityHSCodeForm commodity={finalCommodity} />
            )}
          </div>

          <div>
            {finalCommodity && (
              <CommodityPhotoCover
                commodity={finalCommodity}
                width={95}
                height={95}
                border={true}
                countBoxHeight={26}
                onClick={() => setShowPhotoBrowser(true)}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const CommodityArrayItem2: React.FC<CommodityArrayItemProps> = (props) => {
  const { commodity, id, onDelete } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { isEditing } = useContext(CustomsInstructionContext2);

  const isAdmin = useIsAdmin();

  const commodityQuery = isAdmin
    ? dashboardCommodityApis.useGet('/dashboard/commodity/:id', {
        params: {
          id,
        },
      })
    : commodityApis.useGet('/commodity/:id', {
        params: {
          id,
        },
      });

  const [showPhotoBrowser, setShowPhotoBrowser] = useState(false);

  const finalCommodity = commodityQuery.data ?? commodity;

  return (
    <>
      <CommodityPhotoBrowser
        open={showPhotoBrowser}
        urls={finalCommodity?.images ?? []}
        onClose={() => setShowPhotoBrowser(false)}
      />
      <div className={clsx(classes.commodityDisplay, isAdmin && classes.admin)}>
        <div className={classes.commodityDisplayTitle}>
          <div title={finalCommodity?.name_en} className={classes.title}>
            {finalCommodity?.name_en}
          </div>

          {isEditing && (
            <div className={classes.editLinkContainer}>
              {!isAdmin && (
                <a
                  className={clsx(classes.link, classes.smallerText, classes.whitespacePre)}
                  href={`${url('/utilities/Commodity/CommodityDetails')}?id=${
                    finalCommodity?.id
                  }&action=edit`}
                  target="_blank"
                >
                  {formStrings.edit}
                </a>
              )}
              <div style={{ width: 8, flexShrink: 0 }} />
              <a
                className={clsx(
                  classes.link,
                  classes.smallerText,
                  classes.textGray500Important,
                  classes.whitespacePre,
                )}
                onClick={onDelete}
              >
                {formStrings.delete}
              </a>
            </div>
          )}
        </div>

        <div className={classes.flexInline} style={{ columnGap: 20 }}>
          <div style={{ flex: 1, minWidth: 0, flexShrink: 1 }} className={classes.flexColumn}>
            <div className={classes.commodityInfoContainer}>
              <div
                className={classes.middleText}
                style={{ marginBottom: '0.25rem', gridColumn: 'span 2' }}
              >
                {finalCommodity?.name_ja}
              </div>

              <div>{commodityStrings.commodity_no}:</div>
              <div>{finalCommodity?.commodity_no || '-'}</div>

              <div>{commodityStrings.memo}:</div>
              <div>{finalCommodity?.memo || '-'}</div>

              <div>{commodityStrings.explanation}:</div>
              <div>{finalCommodity?.explanation || '-'}</div>

              <div>{commodityStrings.url}:</div>
              <div>
                {finalCommodity?.url ? (
                  <a
                    href={finalCommodity.url}
                    target="_blank"
                    className={classes.link}
                    style={{ wordBreak: 'break-all' }}
                  >
                    {finalCommodity.url}
                  </a>
                ) : (
                  '-'
                )}
              </div>

              <div>{commodityStrings.usage}:</div>
              <div>{finalCommodity?.usage || '-'}</div>

              <div>{commodityStrings.material}:</div>
              <div>{finalCommodity?.material || '-'}</div>
            </div>

            {isAdmin && finalCommodity && <CommodityHSCodeForm commodity={finalCommodity} />}
          </div>

          <div>
            {finalCommodity && (
              <CommodityPhotoCover
                commodity={finalCommodity}
                width={95}
                height={95}
                border={true}
                countBoxHeight={26}
                onClick={() => setShowPhotoBrowser(true)}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const CommodityArrayInput2: React.FC<{
  field: MobxZodArrayField<ZodArray<ZodNumber>>;
}> = observer(({ field }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [dialogOpen, setDialogOpen] = useState(false);

  const { customsInstruction, isEditing, d2dNo } = useContext(CustomsInstructionContext2);

  const value = getDecodeResultOr(field.decodeResult, [] as number[]);

  const isAdmin = useIsAdmin();

  return (
    <>
      <div className={classes.commodityArrayInputContainer}>
        {value.map((id, index) => (
          <CommodityArrayItem2
            key={id}
            id={id}
            commodity={customsInstruction.commodities.find((item) => item.id === id)}
            onDelete={() => field.splice(index, 1, [])}
          />
        ))}

        <div>
          <Button
            color="outlined"
            disabled={!isEditing}
            size="small"
            type="button"
            text={isAdmin ? strings.selectFromCustomerCommodities : strings.selectFromCommodities}
            onClick={() => setDialogOpen(true)}
          />
          <div style={{ height: 10 }} />
          <InstructionHint>{strings.commodityHint}</InstructionHint>
          <div style={{ height: 10 }} />
          <ErrorMessages field={field} />

          {isAdmin && (
            <Button
              style={{ marginTop: '0.5rem' }}
              color="primary"
              disabled={value.length === 0}
              size="small"
              type="button"
              text={strings.printPdf}
              onClick={() =>
                window.open(`${url('/admin/booking/CustomsCommodityExport')}?d2dNo=${d2dNo}`)
              }
            />
          )}
        </div>
      </div>

      {dialogOpen && (
        <CommodityFilter
          open
          user_id={customsInstruction.writer_id!}
          onClose={(ids) => {
            setDialogOpen(false);
            field.setTouched(true);
            field.setOutput(ids);
          }}
          disabledIds={value}
          isAdmin={isAdmin}
        />
      )}
    </>
  );
});
